<template>
  <div>
    <popup-hint hint="wine">
      <template v-slot:breadcrumbs>
        <breadcrumbs home="Wine">
          <template v-slot:left v-if="lowInventoryCount">
            <list-notification v-model="showStatus">
              <v-list-item>
                <v-icon>mdi-arrow-right-thin</v-icon>
                  {{lowInventoryCount}} wine{{lowInventoryCount>1?'s':''}} with low inventory
                <v-spacer/>
                <v-btn small color="warning" @click="showStatus=false;$nextTick(() => {filters.siteId=null;filters.status='1';filters.type='';filters.search='';filters.lowInventory='1';filters.asof=today})">View</v-btn>
              </v-list-item>
            </list-notification>
          </template>
        </breadcrumbs>
      </template>
      <template v-slot:top>
        If you have a User License, wine can be used for distilling or blending.
      </template>
      <template v-slot:bottom>
        You can create wine types individually or do a batch import of wines.
      </template>
      <template v-slot:actions="{close}">
        <v-btn color="primary" class="ml-4" small @click="close();$refs.import.doImport()">Import</v-btn>
        <v-btn @click="close()" :to="{name:'WineEditNew'}" color="primary" class="ml-4" small>Create</v-btn>
      </template>
    </popup-hint>

    <import-csv v-model="doImport" type="wine" :fields="importFields" importAction="wineImport" :instructions="instructions" ref="import" @refresh="$refs.table.buildList()"/>

    <purtrak-table name="wine" objectType="wines" :defaultColumns="defaultColumns" :headers="headers" ref="table" show-select
                   listEndPoint="wines2" v-model="selected"
                   fields="id,name,active,type,sku,preferredVendor{id},reorderPoint,onOrder,onSalesOrder,lowInventory,
                            volume,value,
                            tankVolume,tankValue,
                            bulkContainerVolume,bulkContainerValue"
                   totalFields="totalVolume,totalValue,totalTankVolume,totalTankValue,totalBulkContainerVolume,totalBulkContainerValue"
                   :totals.sync="totals"
                   :listFilters="listFilters" :parameters="listParameters"
                   @dblclick="wine => $router.push({name:'WineTransactions',params:{wineId:wine.id},query:{siteId:filters.siteId}})"
                   :item-class="wine => (wine.active ? '' : 'grey--text')"
                   title="Wine"
                   :print-info="{pageSize:'landscape',
                                 subTitle:[`As of ${$formatDate(filters.asof)}`,
                                          filters.lowInventory=='1'?'Low inventory only':'',
                                          filters.status!='1'?(filters.status=='0'?'Inactive':'All'):'',
                                          filters.siteId!=null?siteName:'']}"
                   download-endpoint="winesExportCSV">
      <template v-slot:filter>
        <filter-menu include-reset @reset="filters.siteId=null;filters.status='1';filters.lowInventory='0';filters.type='';filters.asof=today;filters.search=''">
          <v-list-item>
            <filter-search :label="[{text:'Name',value:'name'},{text:'SKU',value:'sku'}]" v-model="filters.search" :search-field.sync="filters.searchField" :search-display.sync="searchDisplay"/>
          </v-list-item>
          <v-list-item>
            <v-checkbox v-model="filters.lowInventory" true-value="1" false-value="0" label="Low inventory only" class="mt-n4"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Status" dense outlined :items="[{value:'2',text:'All'},{divider:true},{value:'1',text:'Active'},{value:'0',text:'Inactive'}]" v-model="filters.status"/>
          </v-list-item>
          <v-list-item v-if="sites.length>1">
            <v-autocomplete label="Site" dense outlined :items="[{id:null,name:'All'},{divider:true},...sites]" v-model="filters.siteId" item-text="name" item-value="id"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Type" dense outlined :items="types" v-model="filters.type"/>
          </v-list-item>
          <v-list-item>
            <date-picker label="As of" v-model="filters.asof" date-only/>
          </v-list-item>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.asof!==today" close small class="ml-1" @click:close="filters.asof=today">As of {{filters.asof | formatDate}}</v-chip>
          <v-chip v-show="filters.search" close small class="ml-1" @click:close="filters.search=''">{{searchDisplay}}</v-chip>
          <v-chip v-show="filters.lowInventory=='1'" close small class="ml-1" @click:close="filters.lowInventory='0'">Low inventory only</v-chip>
          <v-chip v-show="filters.status!='1'" close small class="ml-1" @click:close="filters.status='1'">{{filters.status=='0'?'Inactive':'All'}}</v-chip>
          <v-chip v-show="filters.siteId!=null" close small class="ml-1" @click:close="filters.siteId=null">{{siteName}}</v-chip>
          <v-chip v-show="filters.type!=''" close small class="ml-1" @click:close="filters.type=''">{{types.find(t=>t.value===filters.type).text}}</v-chip>
        </template>
      </template>
      <template v-slot:menu>
        <template v-if="!readOnly">
          <v-list-item :to="{name:'WineEditNew'}"><v-list-item-title>Create wine</v-list-item-title></v-list-item>
          <v-divider/>
        </template>
        <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Wine',to:{name:'Wine'}}]},query:{to:'reports/production/ReportsWineActivity'}}"><v-list-item-title>Wine activity report</v-list-item-title></v-list-item>
        <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Wine',to:{name:'Wine'}}]},query:{to:'reports/inventory/ReportsWinesPeriodSummary'}}"><v-list-item-title>Wine summary report</v-list-item-title></v-list-item>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-divider/>
          <v-list-item @click="doImport=true"><v-list-item-title>Import wine from CSV</v-list-item-title></v-list-item>
        </template>
        <template v-if="$store.getters.company.connectedToAccounting">
          <v-divider/>
          <v-list-item @click="sync"><v-list-item-title>Sync wine with QBO</v-list-item-title></v-list-item>
        </template>
      </template>

      <template v-slot:footer v-if="selected.length">
        <v-icon style="position: absolute" class="mt-3 ml-3">mdi-arrow-up-left</v-icon>
        <wine-batch-edit :wine="selected" @refresh="$refs.table.buildList($event)"/>
      </template>
      <template v-slot:item.name="{item,printing}">
        <template v-if="printing">{{item.name}}</template>
        <template v-else>
          <v-hover v-if="!readOnly" v-slot="{hover}">
            <a @click.stop="$router.push({name:'WineEdit',params:{wineId:item.id}})"
               :class="{'text-decoration-underline':hover}">{{item.name}}</a>
          </v-hover>
          <template v-else>{{item.name}}</template>
          <div v-if="!item.active" class="grey--text text-caption">Inactive</div>
        </template>
      </template>
      <template v-slot:item.type="{value}">
        {{types.find(type => type.value === value).text}}
      </template>
      <template v-slot:item.tankVolume="{item}">
        {{item.tankVolume | number(3)}}
      </template>
      <template v-slot:item.bulkContainerVolume="{item}">
        {{item.bulkContainerVolume | number(3)}}
      </template>
      <template v-slot:item.volume="{item,printing}">
        <template v-if="printing">
          {{item.volume | number(3)}}
        </template>
        <span v-else :class="{'warning--text':item.lowInventory}">{{item.volume | number(3)}}</span>
        <div v-if="!printing && item.lowInventory" class="text-caption">
          <v-btn rounded color="warning lighten-1" x-small :to="{name:'PurchasingPurchaseOrdersEditNew',params:{itemType:'Wine',itemId:item.id}}">Create PO</v-btn>
        </div>
      </template>
      <template v-slot:item.tankValue="{item}">
        {{item.tankValue | number(2)}}
      </template>
      <template v-slot:item.bulkContainerValue="{item}">
        {{item.bulkContainerValue | number(2)}}
      </template>
      <template v-slot:item.value="{item,printing}">
        {{item.value | number(2)}}
      </template>
      <template v-slot:item.onOrder="{item}">
        <template v-if="item.onOrder">
          {{item.onOrder | number(3)}}
        </template>
        <template v-else>{{null}}</template>
      </template>
      <template v-slot:item.onSalesOrder="{item}">
        <template v-if="item.onSalesOrder">
          {{item.onSalesOrder | number(3)}}
        </template>
        <template v-else>{{null}}</template>
      </template>
      <template v-slot:header.action v-if="!readOnly">
        <v-btn fab color="primary" small :to="{name:'WineEditNew'}"><v-icon>mdi-plus</v-icon></v-btn>
      </template>
      <template v-slot:item.action="{item}">
        <v-menu :nudge-width="150" offset-y>
          <template v-slot:activator="{ on }">
            <v-btn small v-on="on" v-if="$vuetify.breakpoint.mobile" outlined>Wine operations</v-btn>
            <v-btn small icon v-on="on" v-else><v-icon small>mdi-dots-vertical</v-icon></v-btn>
          </template>
          <v-list dense>
            <v-list-item :to="{name:'WineTransactions',params:{wineId:item.id},query:{siteId:filters.siteId}}"><v-list-item-title>Transactions</v-list-item-title></v-list-item>
            <v-list-item v-if="company.allowAudit && company.permissions.includes('audit')" :to="{name:'Audit',query:{entityId:item.id}}"><v-list-item-title>Audit history</v-list-item-title></v-list-item>
            <v-divider/>
            <v-list-item :to="{name:'PurchasingPurchaseOrdersEditNew',params:{vendorId:item.preferredVendor?item.preferredVendor.id:'',itemType:'Wine',itemId:item.id}}"><v-list-item-title>Create purchase order</v-list-item-title></v-list-item>
            <v-list-item :to="{name:'PurchasingInventoryReceiptsEditNew',params:{vendorId:item.preferredVendor?item.preferredVendor.id:'',itemType:'Wine',itemId:item.id}}"><v-list-item-title>Create inventory receipt</v-list-item-title></v-list-item>
            <template v-if="!readOnly">
              <v-divider/>
              <v-list-item :to="{name:'WineEdit',params:{wineId:item.id}}"><v-list-item-title>Edit wine</v-list-item-title></v-list-item>
              <v-list-item @click="wine=item;showDelete=true"><v-list-item-title>Delete wine</v-list-item-title></v-list-item>
            </template>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:body.append="{headers,printing}" v-if="!$vuetify.breakpoint.mobile">
        <tr :class="darkMode ? 'total-row-dark' : 'total-row'">
          <template v-for="header of headers">
            <td class="text-right" v-if="header.value==='tankVolume'">
              {{totals.totalTankVolume | number(3)}}
            </td>
            <td class="text-right" v-else-if="header.value==='bulkContainerVolume'">
              {{totals.totalBulkContainerVolume | number(3)}}
            </td>
            <td class="text-right" v-else-if="header.value==='volume'">
              {{totals.totalVolume | number(3)}}
            </td>
            <td class="text-right" v-else-if="header.value==='tankValue'">
              {{totals.totalTankValue | number(2)}}
            </td>
            <td class="text-right" v-else-if="header.value==='bulkContainerValue'">
              {{totals.totalBulkContainerValue | number(2)}}
            </td>
            <td class="text-right" v-else-if="header.value==='value'">
              {{totals.totalValue | number(2)}}
            </td>
            <td v-else-if="!printing || header.print === undefined || header.print"/>
          </template>
        </tr>
      </template>
    </purtrak-table>

    <delete-object v-model="showDelete" :name="wine.name" @delete="performDeleteWine"/>
  </div>
</template>

<script>
import WineBatchEdit from '@/views/alcohol/wine/WineBatchEdit'
import {format} from 'date-fns'

export default {
  name: 'Wine',
  components: {WineBatchEdit},
  data() {
    return {
      filters: {
        lowInventory: '0',
        asof: format(new Date(), 'yyyy-MM-dd'),
        status: '1',
        siteId: null,
        type: '',
        search: '',
        searchField: 'name'
      },
      lowInventoryCount: 0,
      showStatus: false,
      searchDisplay: '',
      today: format(new Date(), 'yyyy-MM-dd'),
      types: [{text:'All',value:''},{divider:true},
              {text:'More than 7%',value:'WineHigh'},
              {text:'More than 1.2% and less than 7%',value:'WineMedium'},
              {text:'Not more than 1.2%',value:'WineWeak'}],
      wine: {},
      totals: {totalVolume:0, totalValue:0,
               totalTankVolume:0, totalTankValue: 0,
               totalBulkContainerVolume:0, totalBulkContainerValue: 0},
      showDelete: false,
      doImport: false,
      importFields: [{name:'Name',mapTo:'',mandatory:true},{name:'Type',mapTo:'',mandatory:true}],
      instructions: `<ol class="mb-4">
                        <li>Name</li>
                        <li>Type (must be one of "high", "medium" or "low" representing "Containing more than 7%", "Containing more than 1.2%, but not more than 7%", or "Containing not more than 1.2%" respectfully)</li>
                      </ol>
                      <em>Note that not all information for a wine can be imported.</em>
                      You should, therefore, check each imported wine to ensure all settings are correct.`,
      defaultColumns: [{ value: 'name', visible:true},
                       { value: 'type', visible:true},
                       { value: 'sku', visible:false},
                       { value: 'onOrder', visible:false},
                       { value: 'onSalesOrder', visible:false},
                       { value: 'tankVolume', visible:true},
                       { value: 'bulkContainerVolume', visible:true},
                       { value: 'volume', visible:true, excludeFromExport:true},
                       { value: 'tankValue', visible:false},
                       { value: 'bulkContainerValue', visible:false},
                       { value: 'value', visible:false, excludeFromExport:true}],
      headers: [{ text: 'WINE', value: 'name', width:'30%',sortable:true},
                { text: 'TYPE', value: 'type', width:'20%',sortable:true},
                { text: 'SKU', value: 'sku', width:'20%',sortable:true},
                { text: 'ON PO', value: 'onOrder', width:'15%',sortable:false,align:'right'},
                { text: 'ON SO', value: 'onSalesOrder', width:'15%',sortable:false,align:'right'},
                { text: 'TANKS LITRES', value: 'tankVolume', width:'15%',align:'right',sortable:true},
                { text: 'CONTAINERS LITRES', value: 'bulkContainerVolume', width:'15%',sortable:true,align:'right'},
                { text: 'TOTAL LITRES', value: 'volume', width:'15%',sortable:true,align:'right'},
                { text: 'TANKS $', value: 'tankValue', width:'13%',align:'right',sortable:true},
                { text: 'CONTAINERS $', value: 'bulkContainerValue', width:'13%',sortable:true,align:'right'},
                { text: 'TOTAL $', value: 'value', width:'16%',sortable:true,align:'right'}]
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    sites() {
      return this.$store.getters.sites(true, {B265:true,B263:true});
    },
    siteName() {
      return this.sites.find(site => site.id === this.filters.siteId)?.name;
    },
    readOnly() {
      return !this.company.permissions.includes('inventory.write') &&
             !this.company.permissions.includes('production.write');
    },
    listFilters() {
      const filters = [];
      if (this.filters.lowInventory == '1')
        filters.push({field:'lowInventory', comparator:'eq', value:'1'});
      if (this.filters.status == '1' || this.filters.status == '0')
        filters.push({field:'active', comparator:'eq', value:this.filters.status == '1' ? 'true' : 'false'});
      if (this.filters.type != '')
        filters.push({field:'type', comparator:'eq', value:this.filters.type});
      if (this.filters.search)
        filters.push({field:this.filters.searchField, comparator:'contains', value:this.filters.search});

      return filters;
    },
    listParameters() {
      const parameters = [];
      if (this.filters.asof)
        parameters.push({name:'asof', value:`${this.filters.asof} 23:59:59`});
      if (this.filters.siteId)
        parameters.push({name:'siteId', value:this.filters.siteId});
      return parameters;
    }
  },
  async created() {
    const results = await this.$apiCall(`query Wines {
                                            winesLowInventory
                                          }`,
                                      {});
    if (!results.error)
      this.lowInventoryCount = results.data.winesLowInventory;

    if (!['WineEdit', 'WineEditNew', 'WineTransactions'].includes(this._previousRoute) &&
        Object.keys(this.$route.query).length === 0 &&
        this.lowInventoryCount && !localStorage.getItem('hideNotification')) {
      this.$nextTick(() => this.showStatus = true);
      setTimeout(() => this.showStatus = false, 10_000);  // Close in 10 seconds
    }
  },
  methods: {
    async performDeleteWine() {
      this.loading = true;
      const results = await this.$apiCall(`mutation Wines($id: ID!) {
                                             objectDelete(id:$id)
                                            }`,
                                          {id:this.wine.id});
      this.loading = false;
      if (!results.error) {
        this.showDelete = false;
        this.$refs.table?.buildList();
        this.$store.commit('setError', {type:'success',message:`Deleted wine ${this.wine.name}`});
      }
    },
    async sync() {
      this.loading = true;
      const results = await this.$apiCall(`mutation Wines {
                                             accountingIntegrationSync(step:"wines") {name}
                                           }`, {});
      this.$refs.table?.buildList();
      if (!results.error)
        this.$store.commit('setError', {type:'success',message:`Synced wine with QBO`});
    }
  }
}
</script>